import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { ArrowLeft, Save, Plus, Trash2, Package, Check, ChevronsUpDown } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getQuoteByCode, updateQuote } from "@/integrations/supabase/quotes";
import { getProducts } from "@/integrations/supabase/products";
import { PersonalizationListEditor, PersonalizationEntry, createEmptyPersonalization } from "@/components/PersonalizationListEditor";
import { cn } from "@/lib/utils";
import { CLOTHING_SIZES } from "@/constants/sizes";
import { useClothingSizes } from "@/hooks/useClothingSizes";
import { AddCustomSizeDialog } from "@/components/AddCustomSizeDialog";
import { useAuth } from "@/components/AuthProvider";
import ArquivosSection from "@/components/ArquivosSection";
import { getArquivos, ArquivoData } from "@/integrations/supabase/arquivos";

interface QuoteItem {
  id: string;
  description: string;
  quantity: number;
  value: number;
  category?: string;
  especificacoes?: Record<string, any>;
}

export default function EditarOrcamento() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { empresa } = useAuth();
  const { sizes: clothingSizes, addSize: addCustomSize, isLoading: isSizesLoading } = useClothingSizes(empresa?.id);
  
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [observations, setObservations] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<string>("A combinar");
  const [paymentConditions, setPaymentConditions] = useState<string>("");
  const [items, setItems] = useState<QuoteItem[]>([
    { id: "1", description: "", quantity: 1, value: 0 }
  ]);
  const [personalizations, setPersonalizations] = useState<PersonalizationEntry[]>([]);
  const [discount, setDiscount] = useState<number>(0);
  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>('fixed');
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [productSearchTerm, setProductSearchTerm] = useState<string>("");
  const [productPopoverOpen, setProductPopoverOpen] = useState(false);
  const [arquivos, setArquivos] = useState<ArquivoData[]>([]);
  const [isLoadingArquivos, setIsLoadingArquivos] = useState(false);

  // Query para buscar produtos do catálogo
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });

  // Normalizar texto para busca (remove acentos, espaços extras, caixa)
  const normalizeSearch = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .replace(/\s+/g, " ")
      .trim();

  // Filtrar produtos baseado no termo de busca
  const filteredProducts = useMemo(() => {
    if (!productSearchTerm.trim()) {
      return products;
    }

    const search = normalizeSearch(productSearchTerm);
    if (!search) return products;

    const searchWords = search.split(" ");

    return products.filter((product) => {
      const base = [
        product.name,
        product.type || "",
        ...(Array.isArray(product.materials) ? product.materials : []),
      ]
        .join(" ")
        .toString();

      const normalizedProduct = normalizeSearch(base);

      // Cada palavra digitada deve existir em alguma parte do texto do produto
      return searchWords.every((word) => normalizedProduct.includes(word));
    });
  }, [products, productSearchTerm]);

  const { data: quoteData, isLoading } = useQuery({
    queryKey: ["quote", id],
    queryFn: () => getQuoteByCode(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (quoteData?.quote) {
      const { quote, items: quoteItems, personalizations: quotePersonalizations } = quoteData;
      setCustomerName(quote.customer_name);
      setCustomerPhone(quote.customer_phone || "");
      setObservations(quote.observations || "");
      setDiscount(quote.discount || 0);
      setDiscountType(quote.discount_type || 'fixed');
      
      if (quoteItems.length > 0) {
        setItems(quoteItems.map(item => ({
          id: item.id,
          description: item.description,
          quantity: item.quantity,
          value: item.unit_value || 0
        })));
      }

      // Remover personalizações duplicadas antes de carregar
      const removeDuplicatePersonalizations = (personalizations: typeof quotePersonalizations = []) => {
        if (!personalizations || personalizations.length === 0) return [];
        
        // Sempre usar conteúdo para verificar duplicatas, mesmo se houver ID
        const seenKeys = new Map<string, typeof personalizations[0]>();
        const uniqueItems: typeof personalizations = [];
        
        personalizations.forEach((item) => {
          // Criar chave baseada no conteúdo (normalizar para comparação)
          const personName = (item.person_name || '').trim().toLowerCase();
          const size = (item.size || '').trim().toLowerCase();
          const quantity = item.quantity || 1;
          const notes = (item.notes || '').trim().toLowerCase();
          
          const key = `${personName}_${size}_${quantity}_${notes}`;
          
          if (!seenKeys.has(key)) {
            seenKeys.set(key, item);
            uniqueItems.push(item);
          }
        });
        
        if (personalizations.length !== uniqueItems.length) {
          console.log(`✅ Removidas ${personalizations.length - uniqueItems.length} personalizações duplicadas ao editar orçamento. Original: ${personalizations.length}, Único: ${uniqueItems.length}`);
        }
        
        return uniqueItems;
      };

      const uniquePersonalizations = removeDuplicatePersonalizations(quotePersonalizations ?? []);

      setPersonalizations(
        uniquePersonalizations.map((item) => ({
          id: item.id ?? createEmptyPersonalization().id,
          personName: item.person_name ?? "",
          size: item.size ?? "",
          quantity: item.quantity ?? 1,
          notes: item.notes ?? "",
        }))
      );

      // Carregar condições de pagamento das observações se existirem
      if (quote.observations) {
        const paymentConditionsMatch = quote.observations.match(/📋 CONDIÇÕES DE PAGAMENTO:\n([\s\S]*?)(?:\n\n|$)/);
        if (paymentConditionsMatch && paymentConditionsMatch[1]) {
          setPaymentConditions(paymentConditionsMatch[1].trim());
        }
      }

    }
  }, [quoteData]);

  // Carregar arquivos quando o orçamento é carregado
  useEffect(() => {
    if (!quoteData?.quote?.id) return;

    const loadArquivos = async () => {
      setIsLoadingArquivos(true);
      try {
        const arquivosCarregados = await getArquivos('quotes', quoteData.quote.id);
        setArquivos(arquivosCarregados);
      } catch (error) {
        console.error("Erro ao carregar arquivos:", error);
      } finally {
        setIsLoadingArquivos(false);
      }
    };

    loadArquivos();
  }, [quoteData?.quote?.id]);

  const addItem = () => {
    const newItem: QuoteItem = {
      id: Date.now().toString(),
      description: "",
      quantity: 1,
      value: 0
    };
    setItems([...items, newItem]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id: string, field: keyof QuoteItem, value: string | number) => {
    setItems(items.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const updateSpecification = (id: string, specKey: string, specValue: string) => {
    setItems(items.map(item =>
      item.id === id
        ? { ...item, especificacoes: { ...item.especificacoes, [specKey]: specValue } }
        : item
    ));
  };

  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.value), 0);
  const discountAmountValue = discountType === 'percentage'
    ? (subtotal * discount / 100)
    : discount;
  const calculateTotal = () => {
    return Math.max(0, subtotal - discountAmountValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!customerName.trim()) {
      toast.error("Nome do cliente é obrigatório");
      return;
    }

    if (items.some(item => !item.description.trim())) {
      toast.error("Todos os itens devem ter descrição");
      return;
    }

    try {
      // Preparar personalizações - sempre enviar array, mesmo se vazio
      const validPersonalizations = personalizations
        .filter((p) => p.personName.trim())
        .map((p) => ({
          person_name: p.personName.trim(),
          size: p.size?.trim() || undefined,
          quantity: p.quantity ?? 1,
          notes: p.notes?.trim() || undefined,
        }));

      console.log("📝 Enviando personalizações para updateQuote:", {
        total: personalizations.length,
        valid: validPersonalizations.length,
        personalizations: validPersonalizations
      });

      // Construir observações com condições de pagamento
      let observationsText = observations.replace(/📋 CONDIÇÕES DE PAGAMENTO:[\s\S]*?(?:\n\n|$)/, "").trim();
      if (paymentConditions.trim()) {
        observationsText = observationsText
          ? `${observationsText}\n\n📋 CONDIÇÕES DE PAGAMENTO:\n${paymentConditions}`
          : `📋 CONDIÇÕES DE PAGAMENTO:\n${paymentConditions}`;
      }

      const result = await updateQuote(id!, {
        customer_name: customerName,
        customer_phone: customerPhone,
        observations: observationsText || undefined,
        discount: discount,
        discount_type: discountType,
        items: items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          value: item.value
        })),
        personalizations: validPersonalizations, // Sempre enviar array, mesmo se vazio
      });
      
      if (result.ok) {
        // Recarregar artes para garantir que estão sincronizadas
        const arquivosAtualizados = await getArquivos('quotes', quoteData?.quote?.id!);
        setArquivos(arquivosAtualizados);

        // Invalidar queries relacionadas para forçar recarregamento
        queryClient.invalidateQueries({ queryKey: ["quote", id] });
        queryClient.invalidateQueries({ queryKey: ["quotes"] });

        toast.success("Orçamento atualizado com sucesso!");
        navigate("/orcamentos");
      } else {
        toast.error(result.error || "Erro ao atualizar orçamento");
      }
    } catch (error) {
      console.error("Erro ao atualizar orçamento:", error);
      toast.error("Erro ao atualizar orçamento");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!quoteData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-foreground mb-4">Orçamento não encontrado</h2>
          <p className="text-muted-foreground mb-4">O orçamento solicitado não foi encontrado.</p>
          <Button onClick={() => navigate("/orcamentos")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar para Orçamentos
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center gap-4 p-4">
          <SidebarTrigger />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/orcamentos")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Editar Orçamento</h1>
            <p className="text-sm text-muted-foreground">Modifique os dados do orçamento</p>
          </div>
        </div>
      </header>

      <div className="p-6 max-w-4xl mx-auto">
        <form onSubmit={handleSubmit}>
          <Card className="border-border animate-fade-in">
            <CardHeader>
              <CardTitle className="text-foreground">Informações do Cliente</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customerName">Nome do Cliente *</Label>
                  <Input
                    id="customerName"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Nome do cliente"
                    required
                    className="border-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customerPhone">Telefone</Label>
                  <Input
                    id="customerPhone"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="(11) 99999-9999"
                    className="border-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deliveryDate">Data de Entrega Estimada</Label>
                <Input
                  id="deliveryDate"
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  className="border-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="paymentMethod">Forma de Pagamento</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger className="border-input">
                      <SelectValue placeholder="Selecione a forma de pagamento" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A combinar">🤝 A combinar</SelectItem>
                      <SelectItem value="Dinheiro">💵 Dinheiro</SelectItem>
                      <SelectItem value="Cartão de Crédito">💳 Cartão de Crédito</SelectItem>
                      <SelectItem value="Cartão de Débito">🏦 Cartão de Débito</SelectItem>
                      <SelectItem value="PIX">📱 PIX</SelectItem>
                      <SelectItem value="Cheque">🏛️ Cheque</SelectItem>
                      <SelectItem value="Transferência">🏦 Transferência</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="paymentConditions">Condições de Pagamento</Label>
                  <Textarea
                    id="paymentConditions"
                    placeholder="Ex: 50% entrada, 50% na retirada; Parcelado em 3x; PRAZO ESTIMADO..."
                    value={paymentConditions}
                    onChange={(e) => setPaymentConditions(e.target.value)}
                    rows={2}
                    className="border-input resize-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="observations">Observações</Label>
                <Textarea
                  id="observations"
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  placeholder="Observações adicionais..."
                  rows={3}
                  className="border-input resize-none"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border animate-fade-in mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-foreground">Itens do Orçamento</CardTitle>
                <Button
                  type="button"
                  onClick={addItem}
                  variant="outline"
                  size="sm"
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Item
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Seletor de Catálogo - Sempre visível */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-blue-600" />
                  <Label className="text-blue-800 font-medium">Adicionar do Catálogo</Label>
                </div>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <Popover open={productPopoverOpen} onOpenChange={setProductPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={productPopoverOpen}
                          className="flex-1 justify-between"
                        >
                          {selectedProduct
                            ? products.find((product) => product.id === selectedProduct)?.name || "Selecione um produto"
                            : "Selecione um produto do catálogo"}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput 
                            placeholder="Pesquisar produto por nome, tipo ou material..." 
                            value={productSearchTerm}
                            onValueChange={setProductSearchTerm}
                          />
                          <CommandList>
                            <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
                            <CommandGroup>
                              {filteredProducts.map((product) => (
                                <CommandItem
                                  key={product.id}
                                  value={`${product.name} ${product.type || ""} ${product.materials?.join(" ") || ""}`}
                                  onSelect={() => {
                                    setSelectedProduct(product.id);
                                    setProductPopoverOpen(false);
                                    setProductSearchTerm("");
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      selectedProduct === product.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium">{product.name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      R$ {product.unit_price.toFixed(2)}
                                      {product.type && ` • ${product.type}`}
                                    </span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <Button
                      type="button"
                      onClick={() => {
                        if (!selectedProduct) {
                          toast.error("Selecione um produto");
                          return;
                        }
                        const product = products.find(p => p.id === selectedProduct);
                        if (product) {
                          // Adicionar o produto como um novo item
                          const newItem: QuoteItem = {
                            id: Date.now().toString(),
                            description: `${product.name}${product.type ? ` - ${product.type}` : ""}${product.materials && product.materials.length > 0 ? ` (${product.materials.join(", ")})` : ""}`,
                            quantity: 1,
                            value: product.unit_price
                          };
                          setItems([...items, newItem]);
                          setSelectedProduct("");
                          toast.success("Produto adicionado ao orçamento!");
                        }
                      }}
                      disabled={!selectedProduct}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Adicionar
                    </Button>
                  </div>
                </div>
                {selectedProduct && (
                  <div className="mt-3 text-sm text-blue-700 bg-blue-100 p-2 rounded">
                    {(() => {
                      const product = products.find(p => p.id === selectedProduct);
                      return product ? (
                        <div>
                          <p><strong>Nome:</strong> {product.name}</p>
                          <p><strong>Preço:</strong> R$ {product.unit_price.toFixed(2)}</p>
                          <p><strong>Tempo estimado:</strong> {product.work_hours}h</p>
                          <p><strong>Tipo:</strong> {product.type}</p>
                          {product.materials && product.materials.length > 0 && (
                            <p><strong>Materiais:</strong> {product.materials.join(", ")}</p>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>

              {items.map((item, index) => (
                <div key={item.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 border border-border rounded-lg">
                  <div className="md:col-span-6">
                    <Label>Descrição *</Label>
                    <Input
                      value={item.description}
                      onChange={(e) => updateItem(item.id, "description", e.target.value)}
                      placeholder="Ex: Camiseta bordada logo empresa"
                      className="border-input"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label>Quantidade</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItem(item.id, "quantity", parseInt(e.target.value) || 1)}
                      className="border-input"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label>Valor Unit. (R$)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.value}
                      onChange={(e) => updateItem(item.id, "value", parseFloat(e.target.value) || 0)}
                      className="border-input"
                    />
                  </div>
                  
                  <div className="md:col-span-1 flex items-end">
                    <Button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={items.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="md:col-span-1 flex items-end">
                    <div className="text-sm font-medium text-foreground">
                      R$ {(item.quantity * item.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                  </div>

                  {/* Especificações */}
                  {item.especificacoes && typeof item.especificacoes === 'object' && Object.keys(item.especificacoes).length > 0 && (
                    <div className="md:col-span-12 border-t pt-3 space-y-2">
                      <p className="text-xs font-semibold text-gray-700">Especificações:</p>
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                        {Object.entries(item.especificacoes)
                          .filter(([key]) => !key.toLowerCase().includes('id'))
                          .map(([key, value]) => (
                            <div key={key} className="space-y-1">
                              <Label htmlFor={`spec-${item.id}-${key}`} className="text-xs text-gray-600">
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                              </Label>
                              <Input
                                id={`spec-${item.id}-${key}`}
                                type="text"
                                value={String(value || '')}
                                onChange={(e) => updateSpecification(item.id, key, e.target.value)}
                                className="h-8 text-xs"
                                placeholder={key}
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <PersonalizationListEditor
                entries={personalizations}
                onChange={setPersonalizations}
                description="Registre aqui cada camiseta ou peça personalizada com nome e tamanho."
                availableSizes={clothingSizes}
                onAddSize={addCustomSize}
                isLoadingSizes={isSizesLoading}
              />

              {quoteData?.quote?.id && (
                <ArquivosSection
                  tableType="quotes"
                  recordId={quoteData.quote.id}
                  arquivos={arquivos}
                  onArquivosUpdated={setArquivos}
                  isExpanded={true}
                />
              )}

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                <div className="space-y-2">
                  <Label htmlFor="discountType">Tipo de Desconto</Label>
                  <Select value={discountType} onValueChange={(val: any) => setDiscountType(val)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                      <SelectItem value="percentage">Percentual (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="discount">Desconto {discountType === 'percentage' ? '(%)' : '(R$)'}</Label>
                  <Input
                    id="discount"
                    type="number"
                    min="0"
                    step={discountType === 'percentage' ? '0.01' : '0.01'}
                    value={discount}
                    onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                    placeholder={discountType === 'percentage' ? '0.00' : 'R$ 0,00'}
                  />
                </div>
              </div>

              {discount > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="finalValue">Valor Final (Com Desconto) - R$</Label>
                  <Input
                    id="finalValue"
                    type="number"
                    step="0.01"
                    readOnly
                    className="border-input bg-green-50"
                    value={(discountType === 'percentage'
                      ? subtotal * (1 - discount / 100)
                      : Math.max(0, subtotal - discount)
                    ).toFixed(2)}
                  />
                </div>
              )}

              <div className="flex justify-end pt-4 border-t border-border">
                <div className="text-right">
                  {discount > 0 && (
                    <>
                      <p className="text-sm text-muted-foreground line-through">
                        R$ {subtotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </p>
                      <p className="text-sm text-red-600 mb-2">
                        -{discountType === 'percentage' ? `${discount.toFixed(2)}%` : `R$ ${discountAmountValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                      </p>
                    </>
                  )}
                  <p className="text-sm text-muted-foreground">Total do Orçamento</p>
                  <p className="text-2xl font-bold text-primary">
                    R$ {calculateTotal().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>

              <div className="flex flex-col-reverse gap-3 pt-4 border-t border-border sm:flex-row sm:items-center">
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/90 flex-1 w-full sm:w-auto"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Alterações
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/orcamentos")}
                  className="border-border w-full sm:w-auto"
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}


